import { TrophyIcon } from "@/components/Icons/trophy-icon";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { LeaderboardCycle } from "@/hooks/useGetActiveLeaderboardCycle";
import { pointsToYen } from "@/utils";
import { ChevronRightIcon, PlusIcon } from "lucide-react";
import { useState } from "react";
import { useLocation } from "react-router-dom";

export const LeaderboardInfoCard = ({
  leaderboardCycle,
  currentRank,
}: { leaderboardCycle: LeaderboardCycle; currentRank?: number }) => {
  const pathname = useLocation().pathname;
  const isLeaderboardPage = pathname.includes("/leaderboard");
  const [isOpenTooltip, setIsOpenTooltip] = useState<boolean>(false);

  const rankEnd = leaderboardCycle.slices[leaderboardCycle.slices.length - 1].rankEnd;

  return (
    <div className="grid grid-cols-3 divide-x-[1px] divide-dashed divide-gray-300 py-[6px] px-[15px] border-b bg-orange/10">
      <div className="col-span-2">
        <div className="flex gap-1">
          <div className="py-1">
            <TrophyIcon className="text-dark-gray w-5 h-5" />
          </div>

          <div>
            <span className="text-dark-gray text-sm font-semibold">月間ランキング報酬</span>
            <div className="mt-1 font-oswald">
              <span className="text-4xl leading-9 font-extrabold text-gold-gradient">
                {pointsToYen(leaderboardCycle.totalAmount)}
              </span>
              <span className="text-gray-700 text-lg font-medium ml-1 text-gray">円</span>
            </div>
          </div>
        </div>
        <p className="text-dark-gray text-[10px] mt-1 font-semibold">
          5月の月間ランキング <span className="text-orange font-bold">上位{rankEnd}名</span> に分配されます
        </p>
      </div>
      <div className="w-full col-span-1 pl-2 md:pl-3 lg:pl-7 pt-2">
        {currentRank && (
          <TooltipProvider>
            <Tooltip delayDuration={0} open={isOpenTooltip} defaultOpen={isOpenTooltip} onOpenChange={setIsOpenTooltip}>
              <TooltipTrigger>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isOpenTooltip) {
                      setIsOpenTooltip(true);
                    }
                  }}
                >
                  <div className="text-dark-gray text-sm font-semibold">現在ランキング</div>
                  <div className="text-3xl font-extrabold text-gray-800 text-end">
                    <span>{currentRank}</span>
                    <span className="text-sm font-semibold ml-1 text-dark-gray">位</span>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent className="flex justify-between items-center bg-orange text-white border-0 z-50 py-[3px] px-2">
                <div className="flex items-center space-x-1 leading-[14px]">
                  <span className="text-[10px] font-semibold">あと</span>
                  <span className="text-sm font-bold relative -top-[1px]">{currentRank}</span>
                  <span className="text-[10px] font-semibold">人抜けば上位入賞！</span>
                </div>

                <PlusIcon className="w-4 h-4 text-white" />
                <div className="absolute -bottom-2 text-orange left-1/2 transform -translate-x-1/2">
                  <TooltipArrow />
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {!isLeaderboardPage && (
          <a
            href="/leaderboard"
            className="w-full text-primary text-[10px] mt-2 inline-block font-semibold hover:underline text-right"
          >
            ランキングを見る <ChevronRightIcon className="inline h-4 w-4 ml-1" />
          </a>
        )}
      </div>
    </div>
  );
};

export type IconProps = React.HTMLAttributes<SVGElement>;
const TooltipArrow = (props: IconProps) => {
  return (
    <svg width={11} height={11} viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M0 0L11 -9.6165e-07L1.73716 10.1049C1.12089 10.7772 9.04057e-07 10.3412 8.24327e-07 9.4292L0 0Z"
        fill="currentColor"
      />
    </svg>
  );
};
